import { useState, useRef, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import type { Geofence } from "../../../../../../store/admin/geofencesStore";
import type { PointCoordinate, GeofenceFormValues } from "../types";
import { LINEAR_BUFFER_DEFAULT } from "../types";
import { calculatePolygonArea } from "../utils/calculations";
import { typeColors } from "../utils/helpers";

const defaultValues: GeofenceFormValues = {
  name: "",
  address: "",
  type: "",
  status_id: "",
  description: "",
  color: "",
  shape: "circular",
  geofenceActions: [],
  notifiedUsers: [],
  devices: [],
  alerts: [],
  timezone: "America/Mexico_City",
  sendEmails: false,
  buffer: LINEAR_BUFFER_DEFAULT,
};

const defaultCircularCoordinates = {
  latitude: 19.4326,
  longitude: -99.1332,
  radius: 500,
};

const defaultPolygonalCoordinates: PointCoordinate[] = [
  { latitude: 19.437, longitude: -99.138 },
  { latitude: 19.429, longitude: -99.128 },
  { latitude: 19.44, longitude: -99.127 },
];

const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
  if (typeof google === "undefined" || !google.maps?.Geocoder) return null;
  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({ location: { lat, lng } });
    return result.results?.[0]?.formatted_address ?? null;
  } catch {
    return null;
  }
};

const forwardGeocode = async (
  address: string
): Promise<{ lat: number; lng: number; formatted: string } | null> => {
  if (typeof google === "undefined" || !google.maps?.Geocoder) return null;
  try {
    const geocoder = new google.maps.Geocoder();
    const result = await geocoder.geocode({ address });
    const match = result.results?.[0];
    if (!match) return null;
    return {
      lat: match.geometry.location.lat(),
      lng: match.geometry.location.lng(),
      formatted: match.formatted_address,
    };
  } catch {
    return null;
  }
};

const defaultLinearCoordinates: PointCoordinate[] = [
  { latitude: 19.4326, longitude: -99.1332 },
  { latitude: 19.44, longitude: -99.14 },
];

interface UseGeofenceFormProps {
  isEdit: boolean;
  editGeofence: Geofence | null;
  onShapeChange?: (shape: string) => void;
  initialLinearPoints?: PointCoordinate[];
}

export function useGeofenceForm({
  isEdit,
  editGeofence,
  onShapeChange,
  initialLinearPoints,
}: UseGeofenceFormProps) {
  const isEditing = useRef(false);
  const hasInitializedRef = useRef(false);
  const prevInitialLinearPointsRef = useRef<PointCoordinate[] | undefined>(undefined);

  const [polygonPoints, setPolygonPoints] = useState<PointCoordinate[]>(
    defaultPolygonalCoordinates
  );
  const [linearPoints, setLinearPoints] = useState<PointCoordinate[]>(
    initialLinearPoints || defaultLinearCoordinates
  );
  const [routeDistance, setRouteDistance] = useState<number>(0);
  const [estimatedTime, setEstimatedTime] = useState<string>("");
  const [polygonArea, setPolygonArea] = useState<number>(0);
  const [denseAxis, setDenseAxis] = useState<PointCoordinate[]>([]);
  const [linearAddresses, setLinearAddresses] = useState<
    { address: string | null; geocoding: boolean }[]
  >([
    { address: null, geocoding: false },
    { address: null, geocoding: false },
  ]);
  const linearGeocodeGenRef = useRef<Record<number, number>>({});

  const form = useForm<Geofence>({
    defaultValues: defaultValues as unknown as Geofence,
    mode: "onChange",
  });

  const { watch, setValue, reset, trigger } = form;
  const shape = watch("shape");

  // Verificar si hay cambios reales
  const hasRealChanges = useCallback(() => {
    if (!isEdit || !editGeofence) return false;

    const currentValues = watch();
    const currentShape = currentValues.shape;

    const compareCoordinates = (current: any, original: any) => {
      if (currentShape === "circular") {
        return (
          current?.coordinates?.latitude === original?.coordinates?.latitude &&
          current?.coordinates?.longitude ===
            original?.coordinates?.longitude &&
          current?.coordinates?.radius === original?.coordinates?.radius
        );
      } else if (currentShape === "polygonal" || currentShape === "linear") {
        const currentPoints = current?.coordinates || [];
        const originalPoints = original?.coordinates || [];
        if (currentPoints.length !== originalPoints.length) return false;

        return currentPoints.every((point: any, index: number) => {
          const origPoint = originalPoints[index];
          return (
            point.latitude === origPoint.latitude &&
            point.longitude === origPoint.longitude
          );
        });
      }
      return true;
    };

    const basicFieldsChanged =
      currentValues.name !== editGeofence.name ||
      currentValues.address !== editGeofence.address ||
      currentValues.type !== editGeofence.type ||
      currentValues.status_id !== editGeofence.status_id ||
      currentValues.description !== editGeofence.description ||
      currentValues.shape !== editGeofence.shape;

    const coordinatesChanged = !compareCoordinates(currentValues, editGeofence);

    const arraysEqual = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v, i) => v === b[i]);

    const devicesChanged = !arraysEqual(
      currentValues.devices || [],
      editGeofence.devices || []
    );
    const notifiedUsersChanged = !arraysEqual(
      currentValues.notifiedUsers || [],
      editGeofence.notifiedUsers || []
    );

    const normalizeGeofenceActions = (arr: string[] | undefined) =>
      (["entry", "exit"] as const).filter((a) => (arr || []).includes(a));

    const geofenceActionsChanged = !arraysEqual(
      normalizeGeofenceActions(currentValues.geofenceActions),
      normalizeGeofenceActions(editGeofence.geofenceActions)
    );

    const timezoneChanged =
      (currentValues.timezone || "") !== (editGeofence.timezone || "");

    const sendEmailsChanged =
      ((currentValues as any).sendEmails ?? false) !==
      ((editGeofence as any).sendEmails ?? false);

    const bufferChanged =
      currentShape === "linear" &&
      (Number((currentValues as any).buffer) || LINEAR_BUFFER_DEFAULT) !==
        (Number((editGeofence as any).buffer) || LINEAR_BUFFER_DEFAULT);

    return (
      basicFieldsChanged ||
      coordinatesChanged ||
      devicesChanged ||
      notifiedUsersChanged ||
      geofenceActionsChanged ||
      timezoneChanged ||
      sendEmailsChanged ||
      bufferChanged
    );
  }, [isEdit, editGeofence, watch]);

  const geocodeLinearPoint = useCallback(
    async (index: number, lat: number, lng: number) => {
      const gen = (linearGeocodeGenRef.current[index] ?? 0) + 1;
      linearGeocodeGenRef.current[index] = gen;

      setLinearAddresses((prev) =>
        prev.map((entry, i) => (i === index ? { ...entry, geocoding: true } : entry))
      );

      const address = await reverseGeocode(lat, lng);

      if (linearGeocodeGenRef.current[index] !== gen) return;

      setLinearAddresses((prev) =>
        prev.map((entry, i) =>
          i === index ? { address, geocoding: false } : entry
        )
      );
      if (index === 0 && address) {
        setValue("address", address, { shouldValidate: true, shouldDirty: true });
      }
    },
    [setValue]
  );

  // Cada extremo mueve SOLO su punto. Trasladar el corredor completo (como hace
  // `handleAddressSelect` para polígonos) impediría fijar inicio y fin por
  // separado, que es justamente lo que un corredor necesita.
  const handleLinearAddressEdit = useCallback(
    async (index: number, rawAddress: string) => {
      const trimmed = rawAddress.trim();
      if (!trimmed) return;

      const gen = (linearGeocodeGenRef.current[index] ?? 0) + 1;
      linearGeocodeGenRef.current[index] = gen;

      setLinearAddresses((prev) =>
        prev.map((entry, i) =>
          i === index ? { address: trimmed, geocoding: true } : entry
        )
      );

      const result = await forwardGeocode(trimmed);

      if (linearGeocodeGenRef.current[index] !== gen) return;

      if (!result) {
        setLinearAddresses((prev) =>
          prev.map((entry, i) => (i === index ? { ...entry, geocoding: false } : entry))
        );
        return;
      }

      setLinearPoints((prev) => {
        const updated = prev.map((point, i) =>
          i === index
            ? { latitude: result.lat, longitude: result.lng }
            : point
        );
        setValue("coordinates", updated);
        return updated;
      });

      setLinearAddresses((prev) =>
        prev.map((entry, i) =>
          i === index ? { address: result.formatted, geocoding: false } : entry
        )
      );
      // La direccion del punto de inicio es la de la geocerca. En lineal el
      // buscador general no se muestra, asi que `address` (requerido) sale de aqui.
      if (index === 0) {
        setValue("address", result.formatted, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    },
    [setValue]
  );

  // Manejar selección de dirección
  const handleAddressSelect = useCallback(
    (address: string, lat: number, lng: number) => {
      setValue("address", address);

      if (shape === "circular") {
        setValue("coordinates.latitude", lat);
        setValue("coordinates.longitude", lng);
      } else if (shape === "polygonal") {
        const firstPoint = polygonPoints[0];
        const latOffset = lat - firstPoint.latitude;
        const lngOffset = lng - firstPoint.longitude;

        const updatedPoints = polygonPoints.map((point) => ({
          latitude: point.latitude + latOffset,
          longitude: point.longitude + lngOffset,
        }));

        setPolygonPoints(updatedPoints);
        setValue("coordinates", updatedPoints);
      } else if (shape === "linear") {
        const firstPoint = linearPoints[0];
        const latOffset = lat - firstPoint.latitude;
        const lngOffset = lng - firstPoint.longitude;

        const updatedPoints = linearPoints.map((point) => ({
          latitude: point.latitude + latOffset,
          longitude: point.longitude + lngOffset,
        }));

        setLinearPoints(updatedPoints);
        setValue("coordinates", updatedPoints);
      }

      trigger("address");
    },
    [shape, polygonPoints, linearPoints, setValue, trigger]
  );

  // Manejar cambios en puntos lineales
  const handleLinearPointsChange = useCallback(
    (newPoints: { lat: number; lng: number }[]) => {
      const isDefaultCoordinates =
        isEdit &&
        newPoints.length === 2 &&
        newPoints[0].lat === 19.4326 &&
        newPoints[0].lng === -99.1332 &&
        newPoints[1].lat === 19.44 &&
        newPoints[1].lng === -99.14;

      if (isDefaultCoordinates) return;

      const newLinearPoints = newPoints.slice(0, 2).map((p) => ({
        latitude: p.lat,
        longitude: p.lng,
      }));

      setLinearPoints((prev) => {
        newLinearPoints.forEach((point, index) => {
          const previous = prev[index];
          const moved =
            !previous ||
            previous.latitude !== point.latitude ||
            previous.longitude !== point.longitude;
          if (moved) geocodeLinearPoint(index, point.latitude, point.longitude);
        });
        return newLinearPoints;
      });
      setValue("coordinates", newLinearPoints);
      trigger("coordinates");
    },
    [isEdit, setValue, trigger, geocodeLinearPoint]
  );

  const handleAxisCalculated = useCallback((path: PointCoordinate[]) => {
    setDenseAxis(path);
  }, []);

  const handleBufferChange = useCallback(
    (value: number) => {
      setValue("buffer", value, { shouldDirty: true });
    },
    [setValue]
  );

  // Manejar cálculo de ruta
  const handleRouteCalculated = useCallback((distance: number) => {
    setRouteDistance(distance);

    const avgSpeedKmh = 50;
    const timeInHours = distance / avgSpeedKmh;
    const timeInMinutes = Math.round(timeInHours * 60);

    if (timeInMinutes < 60) {
      setEstimatedTime(`${timeInMinutes} min`);
    } else {
      const hours = Math.floor(timeInMinutes / 60);
      const minutes = timeInMinutes % 60;
      setEstimatedTime(`${hours}h ${minutes}min`);
    }
  }, []);

  // Resetear formulario
  const resetForm = useCallback(() => {
    setPolygonPoints(defaultPolygonalCoordinates);
    setLinearPoints(defaultLinearCoordinates);
    setRouteDistance(0);
    setEstimatedTime("");
    setDenseAxis([]);
    reset(defaultValues as unknown as Geofence);
    isEditing.current = false;
    hasInitializedRef.current = false;
    prevInitialLinearPointsRef.current = undefined;
  }, [reset]);

  // Preparar datos para submit
  const prepareGeofenceData = useCallback(
    (data: any) => {
      const isPointList = data.shape === "polygonal" || data.shape === "linear";
      const points = isPointList
        ? data.coordinates.map((p: any) => ({
            latitude: Number(p.latitude),
            longitude: Number(p.longitude),
          }))
        : null;

      const base = {
        ...data,
        color: typeColors[data?.type] || typeColors.default,
        coordinates: isPointList
          ? points
          : {
              latitude: parseFloat(data.coordinates.latitude),
              longitude: parseFloat(data.coordinates.longitude),
              radius: parseFloat(data.coordinates.radius),
            },
      };

      if (data.shape !== "linear") {
        const { buffer, coordinates_dense, ...rest } = base;
        return rest;
      }

      return {
        ...base,
        coordinates: points.slice(0, 2),
        buffer: Number(data.buffer) || LINEAR_BUFFER_DEFAULT,
        ...(denseAxis.length >= 2 ? { coordinates_dense: denseAxis } : {}),
      };
    },
    [denseAxis]
  );

  // Efecto para cuando llegan nuevos puntos iniciales (del tracking)
  useEffect(() => {
    if (isEdit) return;

    // Detectar si los puntos iniciales cambiaron de vacío/undefined a tener puntos
    const hadPointsBefore = prevInitialLinearPointsRef.current && prevInitialLinearPointsRef.current.length > 0;
    const hasPointsNow = initialLinearPoints && initialLinearPoints.length > 0;

    if (hasPointsNow && !hadPointsBefore) {
      // Puntos iniciales recién llegaron, establecerlos
      setValue("shape", "linear");
      setValue("coordinates", initialLinearPoints);
      setLinearPoints(initialLinearPoints);
      hasInitializedRef.current = true;
    }

    prevInitialLinearPointsRef.current = initialLinearPoints;
  }, [initialLinearPoints, isEdit, setValue]);

  // Efecto para cambio de forma
  useEffect(() => {
    if (isEdit) return;

    // Si hay puntos iniciales activos, no resetear
    if (initialLinearPoints && initialLinearPoints.length > 0 && hasInitializedRef.current) {
      onShapeChange?.(shape);
      return;
    }

    // Obtener las coordenadas actuales
    const currentCoordinates = form.getValues("coordinates");

    // Determinar si hay coordenadas personalizadas (no son las coordenadas por defecto)
    const isCircularWithCustomCoords = !Array.isArray(currentCoordinates) &&
      currentCoordinates?.latitude !== undefined &&
      currentCoordinates?.latitude !== defaultCircularCoordinates.latitude;

    const isArrayWithCustomCoords = Array.isArray(currentCoordinates) &&
      currentCoordinates.length > 0 &&
      !(currentCoordinates.length === defaultPolygonalCoordinates.length &&
        currentCoordinates.every((p, i) =>
          p.latitude === defaultPolygonalCoordinates[i].latitude &&
          p.longitude === defaultPolygonalCoordinates[i].longitude
        ));

    const hasCustomCoordinates = isCircularWithCustomCoords || isArrayWithCustomCoords;

    // Si hay coordenadas personalizadas, convertirlas al nuevo formato en lugar de resetear
    if (hasCustomCoordinates) {
      if (shape === "polygonal" && !Array.isArray(currentCoordinates)) {
        // Convertir de circular a polygonal: crear un polígono centrado en el punto circular
        const centerLat = currentCoordinates.latitude;
        const centerLng = currentCoordinates.longitude;
        const offsetLat = 0.01;
        const offsetLng = 0.01;

        const newPolygonPoints = [
          { latitude: centerLat + offsetLat, longitude: centerLng + offsetLng },
          { latitude: centerLat - offsetLat, longitude: centerLng + offsetLng },
          { latitude: centerLat - offsetLat, longitude: centerLng - offsetLng },
        ];

        setValue("coordinates", newPolygonPoints);
        setPolygonPoints(newPolygonPoints);
      } else if (shape === "linear" && !Array.isArray(currentCoordinates)) {
        // Convertir de circular a linear: crear una línea con el punto como inicio
        const centerLat = currentCoordinates.latitude;
        const centerLng = currentCoordinates.longitude;
        const offsetLat = 0.01;
        const offsetLng = 0.01;

        const newLinearPoints = [
          { latitude: centerLat, longitude: centerLng },
          { latitude: centerLat + offsetLat, longitude: centerLng + offsetLng },
        ];

        setValue("coordinates", newLinearPoints);
        setLinearPoints(newLinearPoints);
      } else if (shape === "linear" && Array.isArray(currentCoordinates) && currentCoordinates.length > 2) {
        // Convertir de polygonal a linear: usar los dos primeros puntos
        const newLinearPoints = [currentCoordinates[0], currentCoordinates[1]];
        setValue("coordinates", newLinearPoints);
        setLinearPoints(newLinearPoints);
      } else if (shape === "polygonal" && Array.isArray(currentCoordinates) && currentCoordinates.length === 2) {
        // Convertir de linear a polygonal: expandir los dos puntos a un polígono
        const p1 = currentCoordinates[0];
        const p2 = currentCoordinates[1];
        const midLat = (p1.latitude + p2.latitude) / 2;
        const midLng = (p1.longitude + p2.longitude) / 2;

        const newPolygonPoints = [
          { latitude: p1.latitude, longitude: p1.longitude },
          { latitude: p2.latitude, longitude: p2.longitude },
          { latitude: midLat, longitude: midLng },
        ];

        setValue("coordinates", newPolygonPoints);
        setPolygonPoints(newPolygonPoints);
      } else if (shape === "circular" && Array.isArray(currentCoordinates) && currentCoordinates.length > 0) {
        // Convertir de polygonal/linear a circular: usar el primer punto como centro
        const firstPoint = currentCoordinates[0];
        const circularCoords = {
          latitude: firstPoint.latitude,
          longitude: firstPoint.longitude,
          radius: currentCoordinates[0]?.radius || 500,
        };

        setValue("coordinates", circularCoords);
      }

      onShapeChange?.(shape);
      return;
    }

    setValue("address", "");

    if (shape === "polygonal") {
      setValue("coordinates", defaultPolygonalCoordinates);
      setPolygonPoints(defaultPolygonalCoordinates);
    } else if (shape === "circular") {
      setValue("coordinates", defaultCircularCoordinates);
    } else if (shape === "linear") {
      // Solo resetear a default si no hay puntos iniciales
      if (!initialLinearPoints || initialLinearPoints.length === 0) {
        setValue("coordinates", defaultLinearCoordinates);
        setLinearPoints(defaultLinearCoordinates);
      }
    }

    onShapeChange?.(shape);
  }, [shape, isEdit, setValue, onShapeChange, initialLinearPoints, form]);

  // Efecto para actualizar área del polígono
  useEffect(() => {
    const area = calculatePolygonArea(polygonPoints);
    setPolygonArea(area);
    if (shape === "polygonal") {
      setValue("area", area);
    }
  }, [polygonPoints, shape, setValue]);

  // Efecto para cargar datos de edición
  useEffect(() => {
    if (isEdit && editGeofence) {
      isEditing.current = true;

      setValue("name", editGeofence.name);
      setValue("address", editGeofence.address);
      setValue("type", editGeofence.type);
      setValue("description", editGeofence.description);
      setValue("status_id", editGeofence.status_id);
      setValue("devices", editGeofence.devices || []);
      setValue("notifiedUsers", editGeofence.notifiedUsers || []);
      setValue("geofenceActions", editGeofence.geofenceActions || []);
      setValue("timezone", editGeofence.timezone || "America/Mexico_City");
      setValue("sendEmails", (editGeofence as any).sendEmails ?? false);

      if (
        editGeofence.shape === "circular" &&
        !Array.isArray(editGeofence.coordinates)
      ) {
        const circularCoords = {
          latitude: editGeofence.coordinates.latitude,
          longitude: editGeofence.coordinates.longitude,
          radius: editGeofence.coordinates.radius ?? 0,
        };
        setValue("coordinates", circularCoords);
        setValue("shape", "circular");
      } else if (
        editGeofence.shape === "polygonal" &&
        Array.isArray(editGeofence.coordinates)
      ) {
        setPolygonPoints(editGeofence.coordinates);
        setValue("coordinates", editGeofence.coordinates);
        setValue("shape", "polygonal");
      } else if (
        editGeofence.shape === "linear" &&
        Array.isArray(editGeofence.coordinates)
      ) {
        const endpoints = editGeofence.coordinates.slice(0, 2);
        setLinearPoints(endpoints);
        setValue("coordinates", endpoints);
        setValue("shape", "linear");
        setValue("buffer", editGeofence.buffer ?? LINEAR_BUFFER_DEFAULT);
        setDenseAxis(
          Array.isArray(editGeofence.coordinates_dense)
            ? editGeofence.coordinates_dense
            : []
        );
      }
    }
  }, [isEdit, editGeofence, setValue]);

  return {
    form,
    shape,
    polygonPoints,
    setPolygonPoints,
    linearPoints,
    setLinearPoints,
    routeDistance,
    estimatedTime,
    polygonArea,
    hasRealChanges,
    denseAxis,
    linearAddresses,
    handleLinearAddressEdit,
    handleAddressSelect,
    handleLinearPointsChange,
    handleAxisCalculated,
    handleBufferChange,
    handleRouteCalculated,
    resetForm,
    prepareGeofenceData,
    defaultCircularCoordinates,
    defaultPolygonalCoordinates,
    defaultLinearCoordinates,
  };
}
